import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import {
  fetchLeadTree, fetchLeadsInGroup, fetchLeadProvinces, searchLeads,
  countCampaignReady, markContacted, setLeadStatus, setLeadNote, optOutLead,
  fetchUnclearSectors, fetchReviewRows, setRelevanceManually,
  canUndoRelevance, correctRelevance,
  LEAD_STATUSES, STATUS_LABEL,
} from '../../lib/factoryLeads';

// The imported Chinese company-registry directory (9,173 companies), folded on
// the two levels of the national-standard classification that actually group
// usefully: 门类 (18 groups) → 大类 (88). 中类/小类 fragment into 353/769 groups
// and render as row text instead.
//
// Nothing here writes to factory_directory: a lead has no products and no page,
// and must never reach the buyer catalog.

const MANUFACTURING = '制造业';   // opened by default — the only leads we can source from
const PAGE = 200;

const STATUS_TONE = {
  new:       { bg: 'rgba(120,120,120,0.10)', fg: 'var(--ac-muted)',        bd: 'rgba(120,120,120,0.25)' },
  contacted: { bg: 'rgba(154,123,79,0.12)',  fg: '#9A7B4F',                bd: 'rgba(154,123,79,0.32)' },
  replied:   { bg: 'rgba(45,106,79,0.10)',   fg: '#2D6A4F',                bd: 'rgba(45,106,79,0.30)' },
  rejected:  { bg: 'rgba(192,57,43,0.09)',   fg: '#c0392b',                bd: 'rgba(192,57,43,0.28)' },
  converted: { bg: 'rgba(45,106,79,0.18)',   fg: '#1B4332',                bd: 'rgba(45,106,79,0.45)' },
};

function Chip({ children, tone = 'muted', title }) {
  const T = {
    muted:  { bg: 'rgba(120,120,120,0.10)', fg: 'var(--ac-muted)', bd: 'rgba(120,120,120,0.22)' },
    gold:   { bg: 'rgba(154,123,79,0.12)',  fg: '#9A7B4F',         bd: 'rgba(154,123,79,0.30)' },
    warn:   { bg: 'rgba(192,57,43,0.09)',   fg: '#c0392b',         bd: 'rgba(192,57,43,0.26)' },
    good:   { bg: 'rgba(45,106,79,0.10)',   fg: '#2D6A4F',         bd: 'rgba(45,106,79,0.28)' },
  }[tone] || {};
  return (
    <span title={title} style={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 600, lineHeight: 1.6,
      color: T.fg, background: T.bg, border: `1px solid ${T.bd}`,
      borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function StatusChip({ status, isAr }) {
  const t = STATUS_TONE[status] || STATUS_TONE.new;
  const l = STATUS_LABEL[status] || STATUS_LABEL.new;
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700,
      color: t.fg, background: t.bg, border: `1px solid ${t.bd}`,
      borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap',
    }}>{isAr ? l.ar : l.en}</span>
  );
}

function Caret({ open, isAr }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={{
           flexShrink: 0, opacity: 0.65, transition: 'transform .15s ease',
           transform: open ? 'rotate(90deg)' : (isAr ? 'rotate(180deg)' : 'none'),
         }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function WaIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2Zm0 2a8 8 0 1 1-4.1 14.9l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 0 1 12 4Zm-2.4 4c-.2 0-.5.1-.7.4-.3.3-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.7 2.7 4.3 3.7 2.1.8 2.5.7 3 .6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3l-1.8-.9c-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.6 6.6 0 0 1-1.9-1.2 7.3 7.3 0 0 1-1.4-1.7c-.1-.2 0-.4.1-.5l.4-.5.3-.5v-.5l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.6Z" />
    </svg>
  );
}

export default function ConsoleLeads({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const t = (ar, en) => (isAr ? ar : en);

  // Filters
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mfgOnly, setMfgOnly] = useState(false);
  const [exporterOnly, setExporterOnly] = useState(false);
  const [mobileOnly, setMobileOnly] = useState(false);
  const [status, setStatus] = useState('');
  const [province, setProvince] = useState('');
  const [relevance, setRelevance] = useState('');
  const [campaign, setCampaign] = useState(false);

  // Review mode: the classifier's 252 unclear rows, settled by hand one sector
  // at a time. It replaces the fold entirely — it is a queue, not a browse.
  const [review, setReview] = useState(false);
  const [sectors, setSectors] = useState(null);
  const [sector, setSector] = useState(null);       // { zh, ar } | null
  const [reviewRows, setReviewRows] = useState(null);

  const [campaignCount, setCampaignCount] = useState(null);
  const [hits, setHits] = useState(null);        // flat search results
  const [searching, setSearching] = useState(false);

  const [tree, setTree] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const flashRef = useRef(null);

  const [openL1, setOpenL1] = useState({ [MANUFACTURING]: true });
  const [openL2, setOpenL2] = useState({});
  const [groupRows, setGroupRows] = useState({});   // `${l1}|${l2}` -> rows
  const [loadingKey, setLoadingKey] = useState('');

  const filters = useMemo(
    () => ({ search: debounced, mfgOnly, exporterOnly, mobileOnly, status, province, relevance, campaign }),
    [debounced, mfgOnly, exporterOnly, mobileOnly, status, province, relevance, campaign],
  );

  // The campaign counter is its own read: it must show the size of the segment
  // the send batches are drawn from, not whatever the other filters narrow it to.
  const [campaignKey, setCampaignKey] = useState(0);
  useEffect(() => { countCampaignReady().then(setCampaignCount, () => setCampaignCount(null)); }, [campaignKey]);

  useEffect(() => { const id = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(id); }, [search]);
  useEffect(() => { fetchLeadProvinces().then(setProvinces, () => setProvinces([])); }, []);

  useEffect(() => {
    if (!review) return;
    fetchUnclearSectors().then(setSectors, (e) => { setErr(e.message || 'load failed'); setSectors([]); });
  }, [review, campaignKey]);

  useEffect(() => {
    let alive = true;
    if (!review || !sector) { setReviewRows(null); return undefined; }
    setReviewRows(null);
    fetchReviewRows(sector.zh).then(
      (rows) => { if (alive) setReviewRows(rows); },
      (e) => { if (alive) { setErr(e.message || 'load failed'); setReviewRows([]); } },
    );
    return () => { alive = false; };
  }, [review, sector]);

  const settle = async (lead, verdict) => {
    try {
      if (lead.consumer_relevance === 'unclear') await setRelevanceManually(lead.id, verdict);
      else await correctRelevance(lead.id, verdict);   // inside the 24h window
      setReviewRows((rows) => (rows || []).map((r) => (
        r.id === lead.id
          ? { ...r, consumer_relevance: verdict, relevance_source: 'manual', relevance_set_at: new Date().toISOString() }
          : r)));
      setCampaignKey((k) => k + 1);   // a 'consumer' verdict grows the campaign
    } catch (e) { flashRef.current?.(e.message || 'update failed'); }
  };

  // Search bypasses the fold entirely: a hit inside a collapsed sector still has
  // to surface, so results come back flat with their sector shown on the row.
  useEffect(() => {
    let alive = true;
    if (!debounced.trim()) { setHits(null); return undefined; }
    setSearching(true);
    searchLeads(filters).then(
      (rows) => { if (alive) { setHits(rows); setSearching(false); } },
      (e) => { if (alive) { setErr(e.message || 'search failed'); setHits([]); setSearching(false); } },
    );
    return () => { alive = false; };
  }, [debounced, filters]);

  // Changing a filter invalidates every loaded group — the counts move with it.
  useEffect(() => {
    let alive = true;
    if (debounced.trim()) return undefined;   // flat search replaces the tree
    setTree(null); setErr('');
    fetchLeadTree(filters).then(
      (rows) => { if (alive) { setTree(rows); setGroupRows({}); } },
      (e) => { if (alive) { setErr(e.message || 'load failed'); setTree([]); } },
    );
    return () => { alive = false; };
  }, [filters, debounced]);

  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(''), 2600); }, []);
  flashRef.current = flash;

  // Tree rows (flat L1+L2 pairs) → nested groups, keeping the RPC's ordering:
  // L1 alphabetical, L2 by size descending.
  const grouped = useMemo(() => {
    const out = [];
    const byL1 = new Map();
    (tree || []).forEach((r) => {
      if (!byL1.has(r.l1_zh)) {
        const g = { zh: r.l1_zh, ar: r.l1_ar, total: 0, children: [] };
        byL1.set(r.l1_zh, g); out.push(g);
      }
      const g = byL1.get(r.l1_zh);
      g.total += Number(r.n);
      g.children.push({ zh: r.l2_zh, ar: r.l2_ar, n: Number(r.n) });
    });
    // Manufacturing first — it is the only section we actually source from.
    out.sort((a, b) => (a.zh === MANUFACTURING ? -1 : b.zh === MANUFACTURING ? 1 : b.total - a.total));
    return out;
  }, [tree]);

  const totals = useMemo(() => {
    const all = (tree || []).reduce((s, r) => s + Number(r.n), 0);
    const mfg = (tree || []).filter((r) => r.l1_zh === MANUFACTURING).reduce((s, r) => s + Number(r.n), 0);
    return { all, mfg };
  }, [tree]);

  const loadGroup = useCallback(async (l1, l2) => {
    const key = `${l1}|${l2}`;
    setLoadingKey(key);
    try {
      const rows = await fetchLeadsInGroup(filters, l1, l2, { limit: PAGE });
      setGroupRows((m) => ({ ...m, [key]: rows }));
    } catch (e) { setErr(e.message || 'load failed'); }
    setLoadingKey('');
  }, [filters]);

  const toggleL2 = (l1, l2) => {
    const key = `${l1}|${l2}`;
    const next = !openL2[key];
    setOpenL2((m) => ({ ...m, [key]: next }));
    if (next && !groupRows[key]) loadGroup(l1, l2);
  };

  const patchRow = (key, id, patch) => setGroupRows((m) => ({
    ...m, [key]: (m[key] || []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
  }));

  // Opening WhatsApp is the outreach event — stamp it so the same lead is not
  // messaged twice. markContacted only promotes a lead still at 'new'.
  const openWa = async (lead, key) => {
    const number = (lead.mobiles || [])[0];
    if (!number) { flash(t('لا جوال لهذا الليد', 'No mobile on this lead')); return; }
    window.open(`https://wa.me/86${number}`, '_blank', 'noopener');
    if (lead.status === 'new') {
      try {
        await markContacted(lead.id, 'whatsapp');
        const patch = { status: 'contacted', contact_channel: 'whatsapp', contacted_at: new Date().toISOString() };
        if (key) patchRow(key, lead.id, patch);
        else setHits((h) => (h || []).map((r) => (r.id === lead.id ? { ...r, ...patch } : r)));
        setCampaignKey((k) => k + 1);
      } catch (e) { flash(e.message || 'update failed'); }
    }
  };

  const changeStatus = async (lead, key, next) => {
    try {
      await setLeadStatus(lead.id, next);
      if (key) patchRow(key, lead.id, { status: next });
      else setHits((h) => (h || []).map((r) => (r.id === lead.id ? { ...r, status: next } : r)));
      setCampaignKey((k) => k + 1);
    } catch (e) { flash(e.message || 'update failed'); }
  };

  // Opt-out is irreversible — the database refuses any later change — so it is
  // confirmed before it is written, and it drops the lead out of the campaign
  // segment permanently.
  const optOut = async (lead, key) => {
    const name = lead.company_name_ar || lead.company_name_zh;
    if (!window.confirm(t(
      `تسجيل «غير مهتم» نهائي لـ${name}؟

لن يُرسَل له شيء بعد ذلك، والقرار لا يمكن التراجع عنه.`,
      `Record a permanent opt-out for ${name}?

Nothing will be sent to them again. This cannot be undone.`,
    ))) return;
    try {
      await optOutLead(lead.id);
      const stamp = new Date().toISOString();
      if (key) patchRow(key, lead.id, { opt_out_at: stamp, status: 'rejected' });
      else setHits((h) => (h || []).map((r) => (r.id === lead.id ? { ...r, opt_out_at: stamp, status: 'rejected' } : r)));
      setCampaignKey((k) => k + 1);
      flash(t('سُجّل «غير مهتم» — لن يُرسَل له شيء', 'Opted out — nothing will be sent'));
    } catch (e) { flash(e.message || 'update failed'); }
  };

  const saveNote = async (lead, key, note) => {
    if ((lead.note || '') === note) return;
    try {
      await setLeadNote(lead.id, note);
      if (key) patchRow(key, lead.id, { note });
      else setHits((h) => (h || []).map((r) => (r.id === lead.id ? { ...r, note } : r)));
    } catch (e) { flash(e.message || 'update failed'); }
  };

  const filterBtn = (on) => ({
    padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
    border: `1px solid ${on ? 'rgba(154,123,79,0.55)' : 'var(--ac-line, rgba(0,0,0,0.12))'}`,
    background: on ? 'rgba(154,123,79,0.12)' : 'transparent',
    color: on ? '#9A7B4F' : 'var(--ac-muted)', ...font,
  });

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="leads">
      <div className="ac-page" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1" style={font}>{t('دليل المصانع الصينية', 'Chinese Factory Directory')}</h1>
            <p className="ac-sub" style={font}>
              {t('قائمة تواصل مستوردة من السجل التجاري الصيني — ليست مصانع على معبر ولا تظهر للمشتري. مطويّة حسب التصنيف الصناعي؛ افتح فئة لعرض شركاتها.',
                 'An outreach list imported from the Chinese company registry — not MAABAR factories, never shown to buyers. Folded by industry; expand a group to load its companies.')}
            </p>
          </div>
        </div>

        {/* The campaign segment: every targeting rule at once. Send batches are
            drawn from exactly this set, so it is one switch, not five. */}
        <button
          onClick={() => setCampaign((v) => !v)}
          hidden={review}
          style={{
            ...font, width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left',
            padding: '13px 16px', marginBottom: 12, borderRadius: 12, cursor: 'pointer',
            border: `1.5px solid ${campaign ? 'rgba(45,106,79,0.55)' : 'var(--ac-line, rgba(0,0,0,0.12))'}`,
            background: campaign ? 'rgba(45,106,79,0.08)' : 'var(--ac-surface, transparent)',
          }}
        >
          <span aria-hidden="true" style={{
            width: 38, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative',
            background: campaign ? '#2D6A4F' : 'rgba(120,120,120,0.35)', transition: 'background .15s',
          }}>
            <span style={{
              position: 'absolute', top: 3, insetInlineStart: campaign ? 19 : 3,
              width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'inset-inline-start .15s',
            }} />
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontWeight: 800, fontSize: 14, color: campaign ? '#2D6A4F' : 'var(--ac-ink)' }}>
              {t('جاهز للحملة', 'Campaign-ready')}
            </span>
            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 2 }}>
              {t('مصانع سلع استهلاكية · جوال غير مشترك · لم يُتواصل · لم يسجّل «غير مهتم»',
                 'Consumer-goods makers · unshared mobile · never contacted · never opted out')}
            </span>
          </span>
          <span style={{
            fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
            color: campaign ? '#2D6A4F' : 'var(--ac-muted)',
          }}>{campaignCount === null ? '…' : campaignCount}</span>
        </button>

        <button
          onClick={() => { setReview((v) => !v); setSector(null); }}
          style={{
            ...font, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left',
            padding: '11px 16px', marginBottom: 12, borderRadius: 12, cursor: 'pointer',
            border: `1px solid ${review ? 'rgba(154,123,79,0.55)' : 'var(--ac-line, rgba(0,0,0,0.12))'}`,
            background: review ? 'rgba(154,123,79,0.09)' : 'transparent',
          }}
        >
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: review ? '#9A7B4F' : 'var(--ac-ink)' }}>
            {t('مراجعة غير المحسوم', 'Review the unclear')}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
            {t('التصنيف الآلي لم يحسمها — تُحسم يدويًا', 'The classifier could not settle these — settle by hand')}
          </span>
        </button>

        {/* Toolbar */}
        <div className="ac-card" style={{ padding: 14, marginBottom: 14, display: review ? 'none' : undefined }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="ac-input" style={{ ...font, flex: '1 1 240px', minWidth: 200 }}
              placeholder={t('ابحث بالاسم العربي أو الصيني أو الممثل القانوني…', 'Search Arabic / Chinese name or legal rep…')}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            <button disabled={campaign} style={{ ...filterBtn(mfgOnly || campaign), opacity: campaign ? 0.5 : 1, cursor: campaign ? 'not-allowed' : 'pointer' }} onClick={() => setMfgOnly((v) => !v)}>{t('مصانع فقط', 'Manufacturers')}</button>
            <button style={filterBtn(exporterOnly)} onClick={() => setExporterOnly((v) => !v)}>{t('مصدّرون', 'Exporters')}</button>
            <button disabled={campaign} style={{ ...filterBtn(mobileOnly || campaign), opacity: campaign ? 0.5 : 1, cursor: campaign ? 'not-allowed' : 'pointer' }} onClick={() => setMobileOnly((v) => !v)}>{t('عنده جوال', 'Has mobile')}</button>
            <select
              className="ac-input" style={{ ...font, width: 'auto' }} value={campaign ? 'new' : status}
              disabled={campaign} title={campaign ? t('محدَّد بواسطة فلتر الحملة', 'Set by the campaign filter') : ''}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">{t('كل الحالات', 'All statuses')}</option>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{isAr ? STATUS_LABEL[s].ar : STATUS_LABEL[s].en}</option>)}
            </select>
            <select
              className="ac-input" style={{ ...font, width: 'auto' }} value={relevance}
              disabled={campaign} title={campaign ? t('محدَّد بواسطة فلتر الحملة', 'Set by the campaign filter') : ''}
              onChange={(e) => setRelevance(e.target.value)}
            >
              <option value="">{t('كل السلع', 'All goods')}</option>
              <option value="consumer">{t('استهلاكية', 'Consumer')}</option>
              <option value="industrial">{t('صناعية', 'Industrial')}</option>
              <option value="unclear">{t('غير محسوم', 'Unclear')}</option>
            </select>
            <select className="ac-input" style={{ ...font, width: 'auto' }} value={province} onChange={(e) => setProvince(e.target.value)}>
              <option value="">{t('كل المقاطعات', 'All provinces')}</option>
              {provinces.map((p) => (
                <option key={p.province_zh} value={p.province_zh}>
                  {(isAr ? p.province_ar : p.province_zh) || p.province_zh} ({p.n})
                </option>
              ))}
            </select>
          </div>
          {tree && (
            <div style={{ ...font, marginTop: 10, fontSize: 12.5, color: 'var(--ac-muted)' }}>
              {t(`${totals.all} شركة ضمن الفلاتر الحالية — منها ${totals.mfg} في التصنيع.`,
                 `${totals.all} companies match — ${totals.mfg} of them in manufacturing.`)}
            </div>
          )}
        </div>

        {err && <div className="ac-card" style={{ ...font, padding: 12, color: '#c0392b', marginBottom: 12 }}>{err}</div>}

        {/* Flat search results — the fold is bypassed on purpose so a hit inside
            a collapsed sector is still found; each row carries its sector. */}
        {!review && debounced.trim() !== '' && (
          <div className="ac-card" style={{ padding: 14 }}>
            <div style={{ ...font, fontSize: 12.5, color: 'var(--ac-muted)', marginBottom: 10 }}>
              {searching
                ? t('جارٍ البحث…', 'Searching…')
                : t(`${(hits || []).length} نتيجة لـ «${debounced.trim()}» — عبر كل الفئات`,
                    `${(hits || []).length} results for “${debounced.trim()}” — across every sector`)}
            </div>
            {!searching && (hits || []).map((r) => (
              <LeadRow
                key={r.id} lead={r} isAr={isAr} font={font} t={t} showSector
                onWa={() => openWa(r, null)}
                onStatus={(v) => changeStatus(r, null, v)}
                onNote={(n) => saveNote(r, null, n)}
                onOptOut={() => optOut(r, null)}
              />
            ))}
            {!searching && (hits || []).length === 0 && (
              <div style={{ ...font, padding: 8, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('لا نتائج.', 'No matches.')}</div>
            )}
            {!searching && (hits || []).length === PAGE && (
              <div style={{ ...font, padding: '6px 4px', fontSize: 11.5, color: 'var(--ac-muted)' }}>
                {t(`تُعرض أول ${PAGE} نتيجة — ضيّق البحث لرؤية الباقي.`, `Showing the first ${PAGE} — narrow the search to see the rest.`)}
              </div>
            )}
          </div>
        )}

        {!review && debounced.trim() === '' && tree === null && <div className="ac-card" style={{ ...font, padding: 24, color: 'var(--ac-muted)' }}>{t('جارٍ التحميل…', 'Loading…')}</div>}
        {!review && debounced.trim() === '' && tree !== null && grouped.length === 0 && (
          <div className="ac-empty" style={font}>{t('لا نتائج ضمن هذه الفلاتر.', 'Nothing matches these filters.')}</div>
        )}

        {/* Fold level 1: 门类 */}
        {!review && debounced.trim() === '' && grouped.map((g1) => {
          const open1 = !!openL1[g1.zh];
          return (
            <div key={g1.zh || '_'} className="ac-card" style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
              <button
                onClick={() => setOpenL1((m) => ({ ...m, [g1.zh]: !open1 }))}
                style={{
                  ...font, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '14px 16px', background: 'transparent', border: 0, cursor: 'pointer',
                  textAlign: isAr ? 'right' : 'left', flexDirection: isAr ? 'row-reverse' : 'row',
                }}
              >
                <Caret open={open1} isAr={isAr} />
                <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--ac-ink)' }}>
                  {g1.ar || t('غير مصنّف', 'Unclassified')}
                </span>
                <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{g1.zh}</span>
                <span style={{ marginInlineStart: 'auto', fontSize: 13, fontWeight: 700, color: 'var(--ac-muted)' }}>{g1.total}</span>
              </button>

              {/* Fold level 2: 大类 */}
              {open1 && (
                <div style={{ borderTop: '1px solid var(--ac-line, rgba(0,0,0,0.07))' }}>
                  {g1.children.map((g2) => {
                    const key = `${g1.zh}|${g2.zh}`;
                    const open2 = !!openL2[key];
                    const rows = groupRows[key];
                    return (
                      <div key={key || '_'}>
                        <button
                          onClick={() => toggleL2(g1.zh, g2.zh)}
                          style={{
                            ...font, width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                            padding: '10px 16px', paddingInlineStart: 34, background: open2 ? 'rgba(154,123,79,0.05)' : 'transparent',
                            border: 0, borderTop: '1px solid var(--ac-line, rgba(0,0,0,0.05))', cursor: 'pointer',
                            textAlign: isAr ? 'right' : 'left', flexDirection: isAr ? 'row-reverse' : 'row',
                          }}
                        >
                          <Caret open={open2} isAr={isAr} />
                          <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--ac-ink)' }}>
                            {g2.ar || t('غير مصنّف', 'Unclassified')}
                          </span>
                          <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>{g2.zh}</span>
                          <span style={{ marginInlineStart: 'auto', fontSize: 12.5, fontWeight: 700, color: 'var(--ac-muted)' }}>{g2.n}</span>
                        </button>

                        {open2 && (
                          <div style={{ padding: '4px 16px 12px', paddingInlineStart: 34 }}>
                            {loadingKey === key && <div style={{ ...font, padding: 10, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('جارٍ التحميل…', 'Loading…')}</div>}
                            {rows && rows.length === 0 && <div style={{ ...font, padding: 10, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('لا نتائج.', 'No rows.')}</div>}
                            {rows && rows.map((r) => (
                              <LeadRow
                                key={r.id} lead={r} isAr={isAr} font={font} t={t}
                                onWa={() => openWa(r, key)}
                                onStatus={(v) => changeStatus(r, key, v)}
                                onNote={(n) => saveNote(r, key, n)}
                                onOptOut={() => optOut(r, key)}
                              />
                            ))}
                            {rows && rows.length === PAGE && (
                              <div style={{ ...font, padding: '6px 4px', fontSize: 11.5, color: 'var(--ac-muted)' }}>
                                {t(`تُعرض أول ${PAGE} — ضيّق بالبحث أو الفلاتر لرؤية الباقي.`, `Showing the first ${PAGE} — narrow with search or filters to see the rest.`)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Review queue — sectors ordered by how much is left in them, so the
            biggest pile of unclear rows is always the first thing offered. */}
        {review && !sector && (
          <div className="ac-card" style={{ padding: 14 }}>
            {sectors === null && <div style={{ ...font, padding: 8, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('جارٍ التحميل…', 'Loading…')}</div>}
            {sectors && sectors.length === 0 && <div style={{ ...font, padding: 8, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('لا يوجد غير محسوم — انتهت المراجعة.', 'Nothing unclear left — review complete.')}</div>}
            {(sectors || []).map((sc) => (
              <button
                key={sc.l2_zh || '_'}
                onClick={() => setSector({ zh: sc.l2_zh, ar: sc.l2_ar })}
                style={{
                  ...font, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  flexDirection: isAr ? 'row-reverse' : 'row', textAlign: isAr ? 'right' : 'left',
                  padding: '11px 12px', marginBottom: 6, borderRadius: 10, cursor: 'pointer',
                  border: '1px solid var(--ac-line, rgba(0,0,0,0.07))', background: 'transparent',
                }}
              >
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: 13, color: 'var(--ac-ink)' }}>
                    {sc.l2_ar || t('غير مصنّف', 'Unclassified')}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ac-muted)' }}>{sc.l2_zh}</span>
                </span>
                {Number(sc.reviewed) > 0 && (
                  <Chip tone="good">{t(`حُسم ${sc.reviewed}`, `${sc.reviewed} settled`)}</Chip>
                )}
                <span style={{ fontSize: 16, fontWeight: 800, color: Number(sc.n) ? '#9A7B4F' : 'var(--ac-muted)' }}>{sc.n}</span>
              </button>
            ))}
          </div>
        )}

        {review && sector && (
          <div className="ac-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexDirection: isAr ? 'row-reverse' : 'row' }}>
              <button onClick={() => setSector(null)} className="ac-btn ac-btn-sm" style={font}>
                {isAr ? '→ رجوع' : '← Back'}
              </button>
              <span style={{ ...font, fontWeight: 700, fontSize: 13.5, color: 'var(--ac-ink)' }}>{sector.ar || sector.zh}</span>
              <span style={{ ...font, fontSize: 11.5, color: 'var(--ac-muted)' }}>
                {t('اضغط «استهلاكية» أو «صناعية» لحسم الصف. الحسم اليدوي لا يُمسح بإعادة التصنيف.',
                   'Press Consumer or Industrial to settle a row. A manual verdict survives re-classification.')}
              </span>
            </div>
            {reviewRows === null && <div style={{ ...font, padding: 8, fontSize: 12.5, color: 'var(--ac-muted)' }}>{t('جارٍ التحميل…', 'Loading…')}</div>}
            {(reviewRows || []).map((r) => (
              <ReviewRow key={r.id} lead={r} isAr={isAr} font={font} t={t} onSettle={(v) => settle(r, v)} />
            ))}
          </div>
        )}

        {toast && <div className="ac-toast" style={font}>{toast}</div>}
      </div>
    </ConsoleShell>
  );
}

function ReviewRow({ lead, isAr, font, t, onSettle }) {
  const settled = lead.consumer_relevance !== 'unclear';
  const activity = (isAr ? lead.ind_l3_ar : lead.ind_l3_zh) || lead.ind_l3_zh || '';
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      padding: '10px 12px', borderRadius: 10, marginBottom: 6,
      border: '1px solid var(--ac-line, rgba(0,0,0,0.07))',
      background: settled ? 'rgba(45,106,79,0.05)' : 'transparent',
      flexDirection: isAr ? 'row-reverse' : 'row',
    }}>
      <div style={{ flex: '1 1 300px', minWidth: 220, textAlign: isAr ? 'right' : 'left' }}>
        <div style={{ ...font, fontWeight: 700, fontSize: 13, color: 'var(--ac-ink)' }}>
          {lead.company_name_ar || lead.company_name_zh}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ac-muted)', marginTop: 1 }} dir="ltr">{lead.company_name_zh}</div>
        <div style={{ ...font, display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5, justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
          {activity ? <Chip>{activity}</Chip> : <Chip tone="warn">{t('بلا نشاط مُعلن', 'No declared activity')}</Chip>}
          {lead.is_exporter && <Chip tone="gold">{t('مصدّر', 'Exporter')}</Chip>}
        </div>
      </div>
      {settled ? (
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          <Chip tone="good">
            {t(lead.consumer_relevance === 'consumer' ? 'حُسم: استهلاكية' : 'حُسم: صناعية',
               `Settled: ${lead.consumer_relevance}`)}
          </Chip>
          {/* Correctable for 24 hours from the moment it was settled; after that
              the database freezes it like an automatic verdict. */}
          {canUndoRelevance(lead) && (
            <button
              onClick={() => onSettle(lead.consumer_relevance === 'consumer' ? 'industrial' : 'consumer')}
              className="ac-btn ac-btn-sm"
              title={t('تصحيح — متاح 24 ساعة من الحسم', 'Correct it — available for 24 hours after settling')}
              style={{ ...font, fontSize: 11.5, color: 'var(--ac-muted)' }}
            >
              {t(lead.consumer_relevance === 'consumer' ? '↺ اجعلها صناعية' : '↺ اجعلها استهلاكية',
                 lead.consumer_relevance === 'consumer' ? '↺ Make industrial' : '↺ Make consumer')}
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 7 }}>
          <button onClick={() => onSettle('consumer')} className="ac-btn ac-btn-sm"
                  style={{ ...font, fontSize: 12, color: '#2D6A4F', fontWeight: 700 }}>
            {t('استهلاكية', 'Consumer')}
          </button>
          <button onClick={() => onSettle('industrial')} className="ac-btn ac-btn-sm"
                  style={{ ...font, fontSize: 12, color: 'var(--ac-muted)' }}>
            {t('صناعية', 'Industrial')}
          </button>
        </div>
      )}
    </div>
  );
}

const RELEVANCE = {
  consumer:   { ar: 'استهلاكية', en: 'Consumer',   tone: 'good' },
  industrial: { ar: 'صناعية',    en: 'Industrial', tone: 'muted' },
  unclear:    { ar: 'غير محسوم', en: 'Unclear',    tone: 'muted' },
};

function LeadRow({ lead, isAr, font, t, showSector, onWa, onStatus, onNote, onOptOut }) {
  const [note, setNote] = useState(lead.note || '');
  const [noteOpen, setNoteOpen] = useState(false);
  const mobiles = lead.mobiles || [];
  const activity = (isAr ? lead.ind_l3_ar : lead.ind_l3_zh) || lead.ind_l3_zh || '';
  const sector = (isAr ? lead.ind_l2_ar : lead.ind_l2_zh) || lead.ind_l2_zh || '';
  const rel = RELEVANCE[lead.consumer_relevance];
  const optedOut = !!lead.opt_out_at;

  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap',
      padding: '10px 12px', borderRadius: 10, marginBottom: 6,
      border: `1px solid ${optedOut ? 'rgba(192,57,43,0.28)' : 'var(--ac-line, rgba(0,0,0,0.07))'}`,
      background: optedOut ? 'rgba(192,57,43,0.04)' : 'var(--ac-surface, transparent)',
      opacity: optedOut ? 0.72 : 1,
      flexDirection: isAr ? 'row-reverse' : 'row',
    }}>
      <div style={{ flex: '1 1 320px', minWidth: 240, textAlign: isAr ? 'right' : 'left' }}>
        <div style={{ ...font, fontWeight: 700, fontSize: 13.5, color: 'var(--ac-ink)' }}>
          {lead.company_name_ar || lead.company_name_zh}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 1 }} dir="ltr">{lead.company_name_zh}</div>

        <div style={{ ...font, display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
          {optedOut && <Chip tone="warn" title={t('طلب عدم التواصل — نهائي', 'Asked not to be contacted — final')}>{t('غير مهتم', 'Opted out')}</Chip>}
          {rel && <Chip tone={rel.tone} title={t('تصنيف الصلة الاستهلاكية', 'Consumer-relevance class')}>{isAr ? rel.ar : rel.en}</Chip>}
          {showSector && sector && <Chip title={t('القطاع', 'Sector')}>{sector}</Chip>}
          {lead.is_exporter && <Chip tone="gold" title={t('نطاق العمل يذكر التصدير', 'Business scope mentions export')}>{t('مصدّر', 'Exporter')}</Chip>}
          {mobiles.length === 0 && <Chip tone="warn" title={t('تلفون أرضي فقط — الواتساب لا يعمل', 'Landline only — WhatsApp will not work')}>{t('بلا جوال', 'No mobile')}</Chip>}
          {lead.mobile_shared && <Chip tone="warn" title={t('نفس الرقم مسجّل على شركة أخرى في الدليل', 'This number is also on another company in the directory')}>{t('رقم مشترك', 'Shared number')}</Chip>}
          {activity && <Chip>{activity}</Chip>}
        </div>

        <div style={{ ...font, fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 5 }}>
          {[lead.legal_rep_ar, isAr ? lead.province_ar : lead.province_zh, lead.district_zh].filter(Boolean).join(' · ')}
        </div>

        {noteOpen && (
          <input
            className="ac-input" autoFocus
            style={{ ...font, marginTop: 7, fontSize: 12.5 }}
            placeholder={t('ملاحظة داخلية…', 'Internal note…')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => { onNote(note); setNoteOpen(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
        )}
        {!noteOpen && lead.note && (
          <button onClick={() => setNoteOpen(true)}
                  style={{ ...font, marginTop: 6, fontSize: 11.5, color: 'var(--ac-muted)', background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: isAr ? 'right' : 'left' }}>
            📝 {lead.note}
          </button>
        )}
      </div>

      <div style={{ flex: '0 0 auto', display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
        {mobiles.length > 0 && <span dir="ltr" style={{ fontSize: 11.5, color: 'var(--ac-muted)', fontVariantNumeric: 'tabular-nums' }}>+86 {mobiles[0]}</span>}

        <button
          className="ac-btn ac-btn-sm" onClick={onWa} disabled={mobiles.length === 0 || optedOut}
          title={optedOut ? t('سجّل «غير مهتم» — لا يُرسَل له شيء', 'Opted out — nothing is sent')
            : mobiles.length ? t('افتح واتساب', 'Open WhatsApp') : t('لا جوال', 'No mobile')}
          style={{
            ...font, display: 'inline-flex', alignItems: 'center', gap: 6,
            color: mobiles.length && !optedOut ? '#25D366' : 'var(--ac-muted)',
            opacity: mobiles.length && !optedOut ? 1 : 0.45,
            cursor: mobiles.length && !optedOut ? 'pointer' : 'not-allowed',
          }}
        >
          <WaIcon />{t('واتساب', 'WhatsApp')}
        </button>

        {/* The source file carries no email addresses; this enables only once an
            admin has found one and saved it on the lead. */}
        <button
          className="ac-btn ac-btn-sm" disabled={!lead.email}
          title={lead.email ? lead.email : t('لا إيميل في المصدر — يُضاف يدويًا بعد أول رد', 'No email in the source — added by hand after a first reply')}
          style={{ ...font, opacity: lead.email ? 1 : 0.4, cursor: lead.email ? 'pointer' : 'not-allowed' }}
        >
          ✉︎ {t('إيميل', 'Email')}
        </button>

        <select
          className="ac-input" value={lead.status} onChange={(e) => onStatus(e.target.value)}
          style={{ ...font, width: 'auto', fontSize: 12, padding: '4px 8px' }}
        >
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{isAr ? STATUS_LABEL[s].ar : STATUS_LABEL[s].en}</option>)}
        </select>

        <StatusChip status={lead.status} isAr={isAr} />

        {/* Irreversible, so it is never the easiest button on the row. */}
        {!optedOut && (
          <button
            onClick={onOptOut} className="ac-btn ac-btn-sm"
            title={t('يسجّل رفض التواصل نهائيًا — لا تراجع', 'Records a permanent opt-out — cannot be undone')}
            style={{ ...font, fontSize: 11.5, color: '#c0392b', opacity: 0.75 }}
          >
            {t('غير مهتم', 'Opt out')}
          </button>
        )}

        {!noteOpen && !lead.note && (
          <button onClick={() => setNoteOpen(true)} className="ac-btn ac-btn-sm" style={{ ...font, fontSize: 12 }} title={t('أضف ملاحظة', 'Add note')}>📝</button>
        )}
      </div>
    </div>
  );
}
